import React, { createContext, useContext, useReducer, useEffect, useState, useCallback } from 'react';
import { AppContextType } from './types';
import { AppState } from '@/types';
import { appReducer } from './reducer';
import { initialState } from './initialState';
import { createActions } from './actions';
import { useAuth } from '@/hooks/useAuth';
import { useStatePersistence } from '@/hooks/useStatePersistence';
import { supabase } from '@/integrations/supabase/client';
import { useStorageMonitor } from '@/hooks/useStorageMonitor';
import { useRealtimeUpdates } from '@/hooks/useRealtimeUpdates';
import { loadUserDataFromSupabase } from '@/utils/supabaseDataLoader';
import { saveUserDataToSupabase } from '@/utils/supabaseDataSaver';
import { useWeekReset } from './weekReset';
import { useMonthlyReset } from '@/hooks/useMonthlyReset';

export const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user, loading: authLoading } = useAuth();

  // Monitor storage changes
  useStorageMonitor();

  // Simple Supabase state persistence
  const setAppState = useCallback(async (newState: AppState) => {
    if (!user?.id) return;
    
    try {
      await saveUserDataToSupabase(user.id, newState);
    } catch (error) {
      console.error('Failed to save state to Supabase:', error);
    }
  }, [user?.id]);

  // Use state persistence hook
  useStatePersistence(state, setAppState, isInitialized, loading);

  // Set up real-time updates
  const refreshData = useCallback(async () => {
    if (!user?.id || authLoading || loading) return;
    
    try {
      const loadedData = await loadUserDataFromSupabase(user.id);
      if (loadedData) {
        dispatch({ type: 'LOAD_STATE', payload: loadedData });
      }
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  }, [user?.id, authLoading, loading]);

  // Load user data when authenticated
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id || authLoading) {
        return;
      }
      
      setLoading(true);
      
      try {
        const loadedData = await loadUserDataFromSupabase(user.id);
        
        if (loadedData) {
          // Preserve local habits if they exist and are more than loaded data
          if (state.habits && state.habits.length > loadedData.habits.length) {
            loadedData.habits = state.habits;
          }
          
          dispatch({ type: 'LOAD_STATE', payload: loadedData });
        }
        
        // Mark as authenticated - prioritize loaded profile name over auth metadata
        dispatch({ 
          type: 'LOGIN', 
          payload: { 
            user: {
              id: user.id, 
              name: loadedData?.user?.name || user.user_metadata?.name || user.email || 'User',
              email: user.email || '',
              weeklyGoal: loadedData?.user?.weeklyGoal ?? loadedData?.weeklyGoal ?? 300,
              profilePicture: loadedData?.user?.profilePicture
            }
          } 
        });
        
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    loadUserData();
  }, [user?.id, authLoading]);

  // Enable real-time updates
  useRealtimeUpdates(refreshData);

  // Apply weekly reset check with database persistence
  useWeekReset(state, dispatch, setAppState);
  
  // Apply monthly reset check
  useMonthlyReset(state, dispatch, setAppState);

  // Create actions with proper state and dispatch
  const actions = createActions(state, dispatch, setAppState);

  // Create context value
  const contextValue: AppContextType = {
    state,
    loading,
    dispatch,
    ...actions
  };
  
  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  
  if (!context) {
    // Return a fallback context instead of throwing
    return {
      state: initialState,
      loading: true,
      dispatch: () => {},
      ...createActions(initialState, () => {}, async () => {})
    };
  }
  
  return context;
}